/**
 * Arranque y pegamento entre el motor del juego y la interfaz.
 *
 * La interfaz es DOM directo, sin framework (ver DECISIONS.md D-006): el
 * presupuesto son 1,5 MB con Three.js dentro, y once pantallas que son listas y
 * botones no justifican React.
 */

import {
  Motor,
  type EstadoPartida,
  type Muerte,
  type ResultadoPartida,
} from './game/motor';
import { audio } from './game/audio';
import { AVATAR_POR_DEFECTO } from './game/avatar';
import type { VistaAvatar } from './game/vistaAvatar';
import { telegram } from './net/telegram';
import {
  api,
  clan,
  duelos,
  modoDev,
  pedir,
  referidos,
  urlTarjetaRecord,
  type Duelo,
  type Perfil,
} from './net/api';
import {
  anuncios,
  devGastarX2,
  devReiniciarRevivires,
  type ConfigAnuncios,
} from './net/anuncios';
import { monedero, type EstadoMonedero } from './net/monedero';
import { fijarIdioma, numero, t, td } from './i18n';
import { el } from './ui/dom';
import type { ContextoAvatar } from './ui/pantallaAvatar';

const lienzo = el<HTMLCanvasElement>('lienzo');
const hud = el('hud');
const panelInicio = el('panelInicio');
const panelFin = el('panelFin');
const panelPausa = el('panelPausa');
const panelRevivir = el('panelRevivir');
const comboTxt = el('combo');
const potenciadores = el('potenciadores');

let perfil: Perfil = {
  record: 0,
  monedas: 0,
  duelosGanados: 0,
  racha: 0,
  avatar: AVATAR_POR_DEFECTO,
  desbloqueos: [],
  avatarElegido: false,
  enlaceInvitacion: '',
  puntosPozo: 0,
  partidasValidasSemana: 0,
  // Cero hasta que llegue `/me`: asi la pantalla del pozo no promete un
  // requisito inventado antes de saber el de verdad.
  partidasParaPozo: 0,
};
let motor: Motor | null = null;
let runActual: { runId: string; firma: string } | null = null;
let ultimoResultado: ResultadoPartida | null = null;
/** El duelo que se esta jugando ahora mismo, si la partida es de un reto. */
let dueloEnCurso: Duelo | null = null;
/** Lo que el servidor dice que se puede ofrecer. Nunca se decide aqui. */
let cfgAnuncios: ConfigAnuncios | null = null;
/** Revivires que quedan en la partida en curso, segun el servidor. */
let revivirRestantes = 0;
/**
 * Lo que cuesta revivir pagando, **segun el servidor**.
 *
 * Empieza en 0 a proposito: con 0 no se ofrece nada. Si la peticion de inicio
 * no trajera precio, el boton no sale, que es mejor que salir con un precio
 * inventado aqui y que el servidor cobre otro.
 */
let precioRevivir = 0;
/**
 * True mientras el jugador se esta yendo al inicio a proposito.
 *
 * Hace falta porque irse al inicio cierra la partida, y cerrarla dispara
 * `alTerminar`, que ensena la pantalla de fin. Sin esta bandera, darle al
 * boton de atras te llevaba al resumen de la partida en vez de al inicio.
 */
let volviendoAlInicio = false;

// ---------------------------------------------------------------------------
// Textos
// ---------------------------------------------------------------------------

function pintarTextos(): void {
  const x = t();
  el('botonJugar').textContent = x.jugar;
  el('etiquetaRecord').textContent = x.record;
  el('etiquetaMonedas').textContent = x.monedas;
  el('etiquetaMonedasFin').textContent = x.monedasGanadas;
  el('etiquetaDistancia').textContent = x.distancia;
  el('tituloFin').textContent = x.finPartida;
  el('botonOtraVez').textContent = x.otraVez;
  el('botonCompartir').textContent = x.compartirRecord;
  el('botonRetar').textContent = td('retar');
  el('tituloPausa').textContent = x.pausado;
  el('botonContinuar').textContent = x.continuar;
  el('botonPausaReiniciar').textContent = x.otraVez;
  el('botonPausaInicio').textContent = x.volverInicio;
  el('botonFinInicio').textContent = x.volverInicio;
  el('botonNoRevivir').textContent = x.noRevivir;
  el('revivirEtiquetaDistancia').textContent = x.distancia;
  el('revivirEtiquetaMonedas').textContent = x.monedas;
  // El texto del boton de revivir se pinta sin pisar la cuenta de los que
  // quedan, que vive en un `span` dentro.
  const marca = el('revivirMarca');
  el('botonRevivir').textContent = x.revivir;
  el('botonRevivir').appendChild(marca);
  el('botonClan').textContent = `🛡️ ${td('miClan')}`;
  el('botonPozo').textContent = `🏆 ${td('pozoTitulo')}`;
  el('pozoTitulo').textContent = td('pozoTitulo');
  el('pozoExplica').textContent = td('pozoExplica');
  el('pozoPuntosEtiqueta').textContent = td('pozoPuntos');
  el('pozoPartidasEtiqueta').textContent = td('pozoPartidas');
  el('pozoMonederoTitulo').textContent = td('pozoMonedero');
  el('pozoConectar').textContent = td('pozoConectar');
  el('pozoQuitar').textContent = td('pozoQuitar');
  el('pozoLetraPequena').textContent = td('pozoLetraPequena');
  el('pozoVolver').textContent = td('pozoVolver');
  el('clanTitulo').textContent = td('miClan');
  el('clanEtiquetaMiembros').textContent = td('clanMiembros');
  el('clanEtiquetaTotal').textContent = td('clanTotal');
  el('botonClanSalir').textContent = td('clanSalir');
  el('botonClanVolver').textContent = x.volverInicio;
  el('apuestaTitulo').textContent = td('cuantoApuestas');
  el('apuestaLetra').textContent = td('rivalPoneIgual');
  el('botonApuestaCancelar').textContent = td('ahoraNo');
  el('retoTitulo').textContent = td('retoRecibido');
  el('botonAceptarReto').textContent = x.aceptarReto;
  el('botonRetoAhoraNo').textContent = td('ahoraNo');
  el('retoMismoNivel').textContent = td('mismoNivel');
  el('tutorialTitulo').textContent = x.tutorialTitulo;
  el('tutorialCarril').textContent = x.tutorialCarril;
  el('tutorialSaltar').textContent = x.tutorialSaltar;
  el('tutorialAgachar').textContent = x.tutorialAgachar;
  el('botonTutorialListo').textContent = x.tutorialVamos;
  el('pieLegal').textContent = x.legalPuntosTemporada;
  el('tituloAvatar').textContent = x.miPersonaje;
  el('avatarGuardar').textContent = x.avatarGuardar;
  el('avatarVolver').textContent = x.volverInicio;
  const etiquetaHud = hud.querySelector('#marcadorMonedas .etiqueta');
  if (etiquetaHud) etiquetaHud.textContent = x.monedas;
}

// ---------------------------------------------------------------------------
// Pantallas
// ---------------------------------------------------------------------------

type Pantalla =
  | 'inicio'
  | 'juego'
  | 'fin'
  | 'pausa'
  | 'invitar'
  | 'revivir'
  | 'avatar'
  | 'tutorial'
  | 'reto'
  | 'apuesta'
  | 'clan'
  | 'pozo';

function mostrar(p: Pantalla): void {
  panelInicio.classList.toggle('visible', p === 'inicio');
  panelFin.classList.toggle('visible', p === 'fin');
  panelPausa.classList.toggle('visible', p === 'pausa');
  panelRevivir.classList.toggle('visible', p === 'revivir');
  el('panelInvitar').classList.toggle('visible', p === 'invitar');
  el('panelAvatar').classList.toggle('visible', p === 'avatar');
  el('panelTutorial').classList.toggle('visible', p === 'tutorial');
  el('panelReto').classList.toggle('visible', p === 'reto');
  el('panelApuesta').classList.toggle('visible', p === 'apuesta');
  el('panelClan').classList.toggle('visible', p === 'clan');
  el('panelPozo').classList.toggle('visible', p === 'pozo');
  hud.classList.toggle('visible', p === 'juego');

  // La vista previa del avatar es un contexto de WebGL abierto. Fuera de su
  // pantalla se suelta: un navegador aguanta pocos a la vez, y quedarse sin
  // uno justo al darle a Jugar seria cambiar un adorno por el juego entero.
  if (p !== 'avatar') soltarVistaAvatar();

  // La del inicio NO se suelta, solo se para: se vuelve a ella constantemente
  // (al acabar cada partida), y recargar el modelo cada vez se nota. Es un
  // contexto que se queda abierto toda la sesion, a cambio de que el menu
  // aparezca entero de golpe en vez de ir montandose a trozos.
  if (p === 'inicio') void abrirVistaInicio();
  else vistaInicio?.parar();

  // El boton de atras de Telegram: en el juego sirve para pausar, en los
  // paneles para volver al inicio. Fuera del inicio siempre hay algo que hacer.
  if (p === 'inicio') {
    telegram.ocultarAtras();
  } else {
    telegram.mostrarAtras(() => {
      if (p === 'juego') pausar();
      else irAlInicio();
    });
  }
}

function pintarInicio(): void {
  el('recordInicio').textContent = numero(perfil.record);
  el('monedasInicio').textContent = numero(perfil.monedas);
  // A quien no ha abierto nunca el creador se le marca el boton. Una vez
  // elegido desaparece: recordarselo a quien ya lo hizo es dar la lata.
  el('botonAvatar').textContent = perfil.avatarElegido
    ? `🧍 ${t().miPersonaje}`
    : `✨ ${t().miPersonaje}`;
}

// ---------------------------------------------------------------------------
// El personaje del menu
// ---------------------------------------------------------------------------

/** El personaje que se ve de pie en el menu. Ver `abrirVistaInicio`. */
let vistaInicio: VistaAvatar | null = null;
let pidiendoVistaInicio = false;

/**
 * Pone al personaje del jugador de pie en la pantalla de inicio.
 *
 * **Por que esto importa mas que el color del fondo.** El jugador se hace un
 * avatar y, hasta ahora, no lo volvia a ver nunca de frente: solo corriendo de
 * espaldas y del tamano de un dedo. Un creador cuyo resultado no se ve es un
 * creador que no sirve de nada.
 *
 * Se trae por detras, igual que en el creador, asi que no pesa en la primera
 * descarga. Si no llega, el menu se queda como estaba: sin muneco, pero
 * entero.
 */
async function abrirVistaInicio(): Promise<void> {
  if (vistaInicio) {
    vistaInicio.mostrar(perfil.avatar);
    vistaInicio.arrancar();
    return;
  }
  if (pidiendoVistaInicio) return;
  pidiendoVistaInicio = true;
  try {
    const { crearVista } = await import('./game/vistaAvatar');
    const vista = crearVista(el<HTMLCanvasElement>('vistaInicio'));
    vistaInicio = vista;
    vista.mostrar(perfil.avatar);
    // Mientras se cargaba el jugador ha podido irse a jugar.
    if (panelInicio.classList.contains('visible')) vista.arrancar();
  } catch (err) {
    console.warn('no pude pintar el personaje en el inicio', err);
  } finally {
    pidiendoVistaInicio = false;
  }
}
// ---------------------------------------------------------------------------
// Creador de personajes: aqui solo el enchufe
// ---------------------------------------------------------------------------
//
// La pantalla entera vive en `ui/pantallaAvatar.ts` y se baja la primera vez
// que alguien toca el boton (23-oct-2026). Aqui se queda lo que tiene que
// existir ANTES de bajar nada: el boton, su enchufe, y lo que hace el juego
// cuando el servidor confirma un avatar nuevo.
//
// Por que: es una pantalla que se abre UNA vez —muchos jugadores no la abren
// nunca— y cobrarle su peso a todo el mundo en la primera carga, que es la
// unica espera que el jugador mira sin poder hacer nada, no se sostiene.

/** El modulo de la pantalla, una vez bajado. `null` = no se ha abierto aun. */
let creador: typeof import('./ui/pantallaAvatar') | null = null;

/** Lo unico que esa pantalla sabe del resto del juego. */
const contextoAvatar: ContextoAvatar = {
  perfil: () => perfil,
  guardado: (avatar) => {
    perfil = { ...perfil, avatar, avatarElegido: true };
    // El personaje que ya esta en la escena del juego se entera tambien: si
    // no, el jugador se cambia de ropa, le da a Jugar y sale con la de antes.
    motor?.cambiarAvatar(avatar);
    // Y el del menu, que es donde va a volver dentro de un segundo.
    vistaInicio?.mostrar(avatar);
    pintarInicio();
  },
  visible: () => el('panelAvatar').classList.contains('visible'),
};

function soltarVistaAvatar(): void {
  creador?.soltarVista();
}

/**
 * Abre el creador de personajes.
 *
 * **El panel se ensena ANTES de pedir el codigo**, y es a proposito: el boton
 * de volver esta en el HTML y lo enchufa esta misma pantalla, asi que aunque
 * el trozo tarde —o no llegue nunca, que con datos moviles pasa— el jugador
 * nunca se queda encerrado en una pantalla que no hace nada.
 */
async function abrirAvatar(): Promise<void> {
  audio.botonUi();
  mostrar('avatar');

  const aviso = el('avatarAviso');
  if (!creador) {
    el('gruposAvatar').textContent = '';
    aviso.className = '';
    aviso.textContent = t().cargando;
  }

  try {
    creador ??= await import('./ui/pantallaAvatar');
  } catch (err) {
    console.warn('no pude traer el creador de personajes', err);
    aviso.className = 'mal';
    aviso.textContent = t().sinConexion;
    return;
  }

  // Entre la peticion y la respuesta el jugador ha podido volverse al inicio.
  if (!contextoAvatar.visible()) return;
  creador.abrir(contextoAvatar);
}

/** Guarda el personaje. El trabajo esta en la pantalla; aqui se delega. */
async function guardarAvatar(): Promise<void> {
  await creador?.guardar();
}

// ---------------------------------------------------------------------------
// Mi clan
// ---------------------------------------------------------------------------
//
// Un clan **es** un grupo de Telegram. Desde aquí solo se mira y se sale: se
// crea escribiendo `/clan` dentro del grupo, que es donde está la gente. Un
// clan creado desde el juego sería un equipo sin sitio donde hablar.

// ---------------------------------------------------------------------------
// El pozo
// ---------------------------------------------------------------------------
//
// Lo que el jugador lleva esta semana, y si va a poder cobrarlo. Los tres
// numeros llegan en `/me`, el requisito de partidas incluido: es el mismo que
// usa el servidor para decidir quien cobra (D-033, ECONOMY 5).
//
// **Esta pantalla no promete ningun importe.** El pozo lo mete el dueno a mano
// cada semana y hasta que Adsgram confirme ingresos de verdad no hay cifra que
// ensenar: un premio inventado es dinero prometido que no existe.

let estadoMonedero: EstadoMonedero | null = null;

/** `0:9a3f...c41d`, que en un movil no cabe entero. */
function abreviarDireccion(d: string): string {
  return d.length <= 16 ? d : `${d.slice(0, 8)}…${d.slice(-6)}`;
}

function pintarNumerosDelPozo(): void {
  el('pozoPuntos').textContent = numero(perfil.puntosPozo);
  el('pozoPartidas').textContent =
    `${numero(perfil.partidasValidasSemana)}/${numero(perfil.partidasParaPozo)}`;

  if (perfil.partidasParaPozo <= 0) {
    // Todavia no ha llegado `/me`, asi que NO se sabe el requisito. Callarse
    // es lo correcto: con el requisito en cero la cuenta daria "ya estas
    // dentro", que es exactamente la mentira mas creible de esta pantalla.
    el('pozoFaltan').textContent = '';
    return;
  }

  const faltan = perfil.partidasParaPozo - perfil.partidasValidasSemana;
  el('pozoFaltan').textContent =
    faltan > 0 ? td('pozoFaltan').replace('{n}', numero(faltan)) : td('pozoDentro');
}

function pintarMonedero(): void {
  const e = estadoMonedero;
  const conectar = el<HTMLButtonElement>('pozoConectar');
  const quitar = el<HTMLButtonElement>('pozoQuitar');

  // Solo una wallet VERIFICADA cuenta: una direccion guardada sin firma no
  // demuestra nada y no cobra (D-034).
  if (e?.verificada && e.direccion) {
    el('pozoDireccion').textContent = abreviarDireccion(e.direccion);
    el('pozoMonederoAviso').textContent = td('pozoVerificada');
    conectar.hidden = true;
    quitar.hidden = false;
  } else {
    el('pozoDireccion').textContent = '—';
    el('pozoMonederoAviso').textContent = td('pozoSinMonedero');
    conectar.hidden = false;
    quitar.hidden = true;
  }
}

async function abrirPozo(): Promise<void> {
  audio.botonUi();
  mostrar('pozo');
  pintarNumerosDelPozo();
  el('pozoDireccion').textContent = t().cargando;
  el('pozoMonederoAviso').textContent = '';

  try {
    estadoMonedero = await monedero.mia();
  } catch (err) {
    // Sin saber el estado del monedero la pantalla sirve igual: los puntos y
    // las partidas ya estan. Se ensena como "sin monedero", que es lo que el
    // jugador puede arreglar desde aqui.
    console.warn('no pude leer el monedero', err);
    estadoMonedero = null;
  }
  pintarMonedero();
}

async function conectarMonedero(): Promise<void> {
  audio.botonUi();
  const boton = el<HTMLButtonElement>('pozoConectar');
  boton.disabled = true;

  const pasos: Record<string, string> = {
    payload: 'pozoPasoPayload',
    abriendo: 'pozoPasoAbriendo',
    esperando: 'pozoPasoEsperando',
    verificando: 'pozoPasoVerificando',
  };

  try {
    // Entre pedir el permiso y volver del monedero pasan varios segundos, y
    // una pantalla quieta parece colgada: por eso va contando por donde va.
    estadoMonedero = await monedero.conectar((paso) => {
      el('pozoMonederoAviso').textContent = td(pasos[paso] ?? 'pozoPasoVerificando');
    });
    telegram.avisar('success');
  } catch (err) {
    console.warn('no pude conectar el monedero', err);
    el('pozoMonederoAviso').textContent = td('pozoFallo');
    telegram.avisar('error');
    boton.disabled = false;
    return;
  }

  boton.disabled = false;
  pintarMonedero();
}

async function quitarMonedero(): Promise<void> {
  audio.botonUi();
  try {
    estadoMonedero = await monedero.quitar();
  } catch (err) {
    console.warn('no pude quitar el monedero', err);
    el('pozoMonederoAviso').textContent = td('pozoFallo');
    return;
  }
  pintarMonedero();
}

async function abrirClan(): Promise<void> {
  audio.botonUi();
  mostrar('clan');
  el('clanNombre').textContent = t().cargando;
  el('clanTabla').textContent = '';
  el('clanExplica').textContent = '';
  el('clanResumen').hidden = true;
  el<HTMLButtonElement>('botonClanSalir').hidden = true;

  try {
    const d = await clan.mio();

    if (!d.tiene) {
      // Sin clan no se enseña una tabla vacía: se explica cómo se consigue
      // uno. Una pantalla que solo dice "no tienes" no sirve de nada.
      el('clanNombre').textContent = td('clanSinClan');
      el('clanExplica').textContent = td('clanComoSeCrea');
      return;
    }

    el('clanNombre').textContent = d.titulo ?? '';
    el('clanMiembros').textContent = numero(d.miembros ?? 0);
    el('clanTotal').textContent = numero(d.total ?? 0);
    el('clanResumen').hidden = false;
    el<HTMLButtonElement>('botonClanSalir').hidden = false;

    const tabla = d.tabla ?? [];
    if (tabla.length === 0) {
      el('clanExplica').textContent = td('clanVacio');
      return;
    }

    const cont = el('clanTabla');
    tabla.forEach((m, i) => {
      const fila = document.createElement('div');
      fila.className = `fila-clan${m.soyYo ? ' yo' : ''}`;
      const puesto = document.createElement('span');
      puesto.className = 'puesto';
      puesto.textContent = `${i + 1}`;
      const nombre = document.createElement('span');
      nombre.className = 'nombre';
      nombre.textContent = m.nombre;
      const marca = document.createElement('span');
      marca.className = 'marca';
      marca.textContent = `${numero(m.record)} m`;
      fila.append(puesto, nombre, marca);
      cont.appendChild(fila);
    });

    // El puesto se dice aparte porque quien no entra en los diez primeros no
    // se ve en la tabla, y es justo a quien más le importa saber dónde va.
    if ((d.puesto ?? 0) > 0) {
      el('clanExplica').textContent = `${td('clanVasElPuesto')} ${d.puesto}`;
    }
  } catch (err) {
    console.warn('no pude cargar el clan', err);
    el('clanNombre').textContent = t().sinConexion;
  }
}

async function salirDelClan(): Promise<void> {
  audio.botonUi();
  try {
    await clan.salir();
  } catch (err) {
    console.warn('no pude salir del clan', err);
  }
  await abrirClan();
}

// ---------------------------------------------------------------------------
// El reto que te espera al entrar
// ---------------------------------------------------------------------------
//
// **Esto es lo que faltaba para que los duelos existieran.** El 24-oct-2026 la
// base tenia 25 partidas y CERO duelos, y no era que nadie quisiera: era que
// el reto no tenia por donde entrar.
//
// El bot manda un boton «Aceptar el reto» que abre
// `t.me/<bot>/app?startapp=duel_<id>`, y ese formato exige una Mini App creada
// en BotFather que no existe. El camino que si funciona —`?start=duel_<id>`—
// entra por el bot, y el boton de Mini App que el bot ensena despues **no
// entrega el parametro**: es un `WebAppInfo` y Telegram no se lo pasa.
//
// Asi que se arregla como ya se arreglo la atribucion de referidos: **lo que
// el enlace no entrega, lo apunta el bot**. Y se mira por las DOS puertas, que
// es la regla de la casa: el parametro de arranque si llega, y el recado que
// dejo el bot si no.

let retoPendiente: Duelo | null = null;

/** El id del reto, venga por donde venga. */
function idDelRetoQueEspera(): string {
  const p = telegram.parametroInicio;
  if (p.startsWith('duel_')) return p.slice('duel_'.length);
  return perfil.dueloPendiente ?? '';
}

/**
 * ¿Me han retado? Se pregunta al abrir el juego.
 *
 * Solo se ofrece lo que de verdad se puede aceptar: un reto propio, uno ya
 * resuelto o uno caducado se olvidan sin enseñar nada. Enseñar un botón que va
 * a dar error es peor que no enseñar nada.
 */
async function mirarSiMeHanRetado(): Promise<void> {
  const id = idDelRetoQueEspera();
  if (!id) return;

  try {
    const duelo = await duelos.ver(id);
    if (duelo.soyRetador || duelo.estado !== 'esperando') {
      await olvidarReto(id);
      return;
    }
    retoPendiente = duelo;
    pintarReto(duelo);
    mostrar('reto');
  } catch (err) {
    // Un reto que no se puede leer no puede dejar al jugador sin menu.
    console.warn('no pude mirar el reto que esperaba', err);
  }
}

function pintarReto(d: Duelo): void {
  const metros = d.distanciaRetador ?? 0;
  const quien = d.retadorNombre || '?';
  el('retoQuien').textContent = t().teReta(quien, metros);
  const apuesta = el('retoApuesta');
  apuesta.textContent = d.apuesta > 0
    ? `${td('apuestaEnJuego')}: ${numero(d.apuesta)} ${d.moneda === 'fichas' ? td('fichas') : t().monedas.toLowerCase()}`
    : '';
  el('retoAviso').textContent = '';
}

/** Que el servidor olvide el recado. Si falla, no pasa nada grave. */
async function olvidarReto(id: string): Promise<void> {
  perfil = { ...perfil, dueloPendiente: null };
  try {
    await duelos.descartar(id);
  } catch (err) {
    console.warn('no pude descartar el reto', err);
  }
}

async function aceptarReto(): Promise<void> {
  audio.botonUi();
  const duelo = retoPendiente;
  if (!duelo) return;

  const boton = el<HTMLButtonElement>('botonAceptarReto');
  boton.disabled = true;
  el('retoAviso').textContent = td('aceptando');

  try {
    // Aceptar COBRA la apuesta en el servidor. Por eso se juega solo si el
    // servidor dijo que si: si se jugara antes, una partida podria contar
    // para un duelo que no llego a aceptarse.
    dueloEnCurso = await duelos.aceptar(duelo.duelId);
    retoPendiente = null;
    perfil = { ...perfil, dueloPendiente: null };
    await jugar();
  } catch (err) {
    console.warn('no pude aceptar el reto', err);
    const mensaje = String(err);
    el('retoAviso').textContent = mensaje.includes('saldo')
      ? td('retoSinSaldo')
      : mensaje.includes('409') || mensaje.includes('403') || mensaje.includes('caduc')
        ? td('retoNoDisponible')
        : t().errorGenerico;
    boton.disabled = false;
  }
}

/** «Ahora no»: al menu, y que no vuelva a salir cada vez que abra el juego. */
async function rechazarReto(): Promise<void> {
  audio.botonUi();
  const duelo = retoPendiente;
  retoPendiente = null;
  mostrar('inicio');
  if (duelo) await olvidarReto(duelo.duelId);
}

// ---------------------------------------------------------------------------
// Tutorial de tres segundos
// ---------------------------------------------------------------------------
//
// El juego se juega deslizando el dedo y **eso no lo explica nadie**. Un
// jugador nuevo toca la pantalla, no pasa nada que entienda, se come el primer
// obstaculo y se va. Son tres frases: a los lados se cambia de carril, arriba
// se salta (o un toque), abajo se agacha.
//
// Tres decisiones, y las tres por el mismo motivo —que esto no estorbe a quien
// ya sabe jugar—:
//
//   1. Sale UNA vez, y solo a quien no ha hecho ni un metro.
//   2. Sale con la partida ya empezada y EN PAUSA. Con el mundo detras se
//      entiende de que se esta hablando; en pausa no se muere leyendo.
//   3. Se cierra solo a los cuatro segundos. Si el jugador no toca nada, el
//      juego sigue: un cartel que espera para siempre es un juego colgado.

/** Donde se recuerda que ya se vio. */
const CLAVE_TUTORIAL = 'moonrush.tutorial.visto';

/** El reloj que lo cierra solo. */
let relojTutorial = 0;

function yaVioElTutorial(): boolean {
  try {
    return localStorage.getItem(CLAVE_TUTORIAL) === '1';
  } catch {
    // Modo privado, almacenamiento bloqueado, un WebView raro... Ante la duda
    // se ENSENA: verlo dos veces molesta un poco, no verlo nunca cuesta el
    // jugador.
    return false;
  }
}

function apuntarQueLoVio(): void {
  try {
    localStorage.setItem(CLAVE_TUTORIAL, '1');
  } catch {
    // Si no se puede guardar, se volvera a ensenar. No es grave.
  }
}

/**
 * Solo a quien no conoce los gestos: la primera vez y sin un metro hecho.
 *
 * Quien tiene record ya sabe jugar, y un cartel encima de la partida de alguien
 * que sabe jugar es una molestia.
 */
function tocaEnsenarTutorial(): boolean {
  return perfil.record === 0 && !yaVioElTutorial();
}

function ensenarTutorial(): void {
  // En medio segundo da tiempo a morir, o a volverse al inicio. Si ya no se
  // esta jugando, no hay nada que explicar ni nada que pausar.
  if (!hud.classList.contains('visible')) return;

  motor?.pausar();
  mostrar('tutorial');
  window.clearTimeout(relojTutorial);
  relojTutorial = window.setTimeout(cerrarTutorial, 4000);
}

/**
 * Cierra el tutorial y sigue la partida.
 *
 * El `if` de la primera linea no es de adorno: el reloj puede llegar DESPUES de
 * que el jugador se haya ido al inicio con el boton de atras, y entonces
 * reanudar aqui seria reanudar una partida que ya no existe.
 */
function cerrarTutorial(): void {
  if (!el('panelTutorial').classList.contains('visible')) return;
  window.clearTimeout(relojTutorial);
  apuntarQueLoVio();
  mostrar('juego');
  motor?.reanudar();
}

// ---------------------------------------------------------------------------
// HUD
// ---------------------------------------------------------------------------

const ICONOS: Record<string, string> = {
  x2: '×2',
  iman: '🧲',
  escudo: '🛡',
  cohete: '🚀',
};

let comboVisibleHasta = 0;

function pintarHud(e: EstadoPartida): void {
  const dist = el('marcadorDistancia').querySelector('.valor');
  if (dist) dist.textContent = numero(e.distancia);
  const mon = el('marcadorMonedas').querySelector('.valor');
  if (mon) mon.textContent = numero(e.monedas);

  // El combo aparece a partir de 3: por debajo de eso es ruido.
  if (e.combo >= 3) {
    comboTxt.textContent = `${e.combo}× ${t().combo}`;
    comboTxt.classList.add('visible');
    comboVisibleHasta = performance.now() + 900;
  } else if (performance.now() > comboVisibleHasta) {
    comboTxt.classList.remove('visible');
  }

  // Potenciadores activos con su barra de tiempo. Se redibuja solo lo que
  // cambia: tocar innerHTML sesenta veces por segundo cuesta fps.
  const activos: Array<[string, number, number]> = [];
  if (e.msX2 > 0) activos.push(['x2', e.msX2, 10000]);
  if (e.msIman > 0) activos.push(['iman', e.msIman, 7000]);
  if (e.escudo) activos.push(['escudo', 1, 1]);
  if (e.msCohete > 0) activos.push(['cohete', e.msCohete, 3200]);

  const clave = activos.map(([k]) => k).join(',');
  if (potenciadores.dataset.clave !== clave) {
    potenciadores.dataset.clave = clave;
    potenciadores.innerHTML = activos
      .map(
        ([k]) =>
          `<div class="potenciador" data-tipo="${k}">${ICONOS[k] ?? '?'}` +
          `<div class="barra" style="width:100%"></div></div>`
      )
      .join('');
  }
  activos.forEach(([, ms, total], i) => {
    const barra = potenciadores.children[i]?.querySelector<HTMLElement>('.barra');
    if (barra) barra.style.width = `${Math.max(0, Math.min(100, (ms / total) * 100))}%`;
  });
}

// ---------------------------------------------------------------------------
// Partida
// ---------------------------------------------------------------------------

async function jugar(usarX2 = false): Promise<void> {
  audio.despertar();
  audio.botonUi();

  // --- La bandera de "me voy al inicio" se baja AQUI ---
  //
  // Una partida que empieza no puede ser una partida de la que alguien se esta
  // yendo. Parece obvio y es la linea que faltaba.
  //
  // Lo que pasaba sin esto, y se vio jugando en el movil el 23-oct-2026:
  //
  //   1. mueres -> se ensena el resumen -> al cerrarse, la bandera se baja
  //   2. tocas Inicio -> `irAlInicio` la SUBE y llama a `motor.abandonar()`
  //   3. pero la partida YA estaba cerrada, asi que `abandonar()` se vuelve
  //      sin llamar a `alTerminar`... que es el unico sitio donde se bajaba
  //   4. la bandera se queda subida PARA SIEMPRE
  //   5. a partir de ahi, cada muerte cree que el jugador se esta yendo al
  //      inicio y **no ensena ningun panel**: muerto y sin salida
  //
  // Se arregla aqui y no en `irAlInicio` a proposito: alli habria que saber si
  // la partida sigue abierta, que es cosa del motor. Bajarla al empezar hace
  // que no pueda arrastrarse de una partida a la siguiente, sea cual sea el
  // camino por el que se llego.
  volviendoAlInicio = false;

  if (!motor) {
    motor = new Motor(lienzo, perfil.avatar);
    motor.alCambiarEstado = pintarHud;
    motor.alMorir = alMorir;
    motor.alTerminar = alTerminar;
    motor.alCasiRecord = () => {
      /* la cinta se pinta en alTerminar, que ya sabe los numeros */
    };
    // Solo en modo desarrollo: deja el motor a mano desde la consola del
    // navegador para poder mirar los biomas sin jugarse 3.000 metros.
    //
    // Va detras de `modoDev`, que se calcula por la AUSENCIA de Telegram, asi
    // que en produccion no existe. Un juego donde se pueda tocar el motor desde
    // la consola es un juego donde cualquiera se pone el record que quiera.
    if (modoDev) {
      (window as unknown as Record<string, unknown>).__motor = motor;
    }
  }

  try {
    // En modo DEV no hay servidor que conceda el x2, asi que el vale se gasta
    // aqui. En produccion esta linea no hace nada: lo gasta el servidor al
    // abrir la partida, que es el unico que sabe si el anuncio se vio.
    const pedirX2 = modoDev && usarX2 ? devGastarX2() : usarX2;
    const inicio = await api.iniciarPartida(dueloEnCurso?.duelId, pedirX2);
    runActual = { runId: inicio.runId, firma: inicio.firma };
    // **El x2 lo concede el servidor.** Aqui se lee lo que contesto, no lo que
    // se pidio: si el jugador pide el x2 sin haber visto el anuncio, el
    // servidor devuelve false y la partida va normal.
    const x2 = inicio.x2 === true;
    revivirRestantes = inicio.revivirRestantes ?? 0;
    precioRevivir = inicio.precioRevivirMonedas ?? 0;
    devReiniciarRevivires();

    mostrar('juego');
    el('cintaFin').className = '';
    // Los botones de anuncio vuelven a su texto: si no, al salir de la partida
    // el jugador se encuentra un "Cargando el anuncio..." que no carga nada.
    for (const id of ['botonX2', 'botonX2Fin']) {
      const b = el<HTMLButtonElement>(id);
      b.textContent = t().jugarConX2;
      b.disabled = false;
    }
    motor.iniciar(inicio.seed, perfil.record, x2 ? 2 : 1);

    // Se le dice que el x2 esta puesto. Si ve el anuncio y luego no nota la
    // diferencia, la proxima vez no lo ve.
    const aviso = el('combo');
    if (x2) {
      aviso.textContent = t().x2Activo;
      aviso.classList.add('visible');
      window.setTimeout(() => aviso.classList.remove('visible'), 2200);
    }

    // Y la primera vez, los gestos: medio segundo DESPUES de arrancar.
    //
    // No en el mismo instante, y esto se vio jugandolo: pausar nada mas
    // empezar congela el juego antes de que se dibuje el primer cuadro, asi
    // que el cartel salia sobre una pantalla NEGRA y la primera imagen del
    // juego era un cartel flotando en el vacio. Con medio segundo corriendo
    // antes, el mundo se ve detras y se entiende de que se esta hablando.
    if (tocaEnsenarTutorial()) window.setTimeout(ensenarTutorial, 600);
  } catch (err) {
    console.error('no pude empezar la partida', err);
    el('subtituloInicio').textContent = t().sinConexion;
  }
}

/**
 * El jugador ha muerto. **La partida sigue abierta**, y aqui se decide.
 *
 * Es el cambio de forma que trae esta fase: antes, morir cerraba la partida en
 * el servidor y no habia nada que revivir. Ahora morir solo ensena una
 * pregunta, y la partida se cierra cuando el jugador contesta.
 *
 * Si no hay anuncios o no quedan revivires, no se le pregunta nada: se cierra
 * directamente. Un dialogo con una sola salida es una molestia, no una opcion.
 */
function alMorir(m: Muerte): void {
  el('revivirDistancia').textContent = numero(m.distancia);
  el('revivirMonedas').textContent = numero(m.monedas);

  // Dos caminos distintos para lo mismo, y los dos pueden faltar:
  //   - el anuncio, que depende de que el servidor los tenga encendidos
  //   - las monedas, que dependen de que el jugador tenga suficientes
  //
  // **Hoy en produccion solo existe el segundo**, porque los anuncios estan
  // apagados. Sin el, morir era el final y punto.
  const conAnuncio = Boolean(cfgAnuncios?.activos) && revivirRestantes > 0;
  const conMonedas =
    precioRevivir > 0 && perfil.monedas >= precioRevivir && revivirRestantes > 0;

  if (!conAnuncio && !conMonedas) {
    motor?.rendirse();
    return;
  }

  el('revivirMarca').textContent = t().revivirRestantes(revivirRestantes);
  el('revivirInfo').textContent = '';

  const boton = el<HTMLButtonElement>('botonRevivir');
  boton.hidden = !conAnuncio;
  boton.disabled = false;

  const pagar = el<HTMLButtonElement>('botonRevivirMonedas');
  pagar.hidden = !conMonedas;
  pagar.disabled = false;
  pagar.textContent = t().revivirPorMonedas(precioRevivir);

  mostrar('revivir');
}

/** Ver el anuncio para seguir jugando. El servidor concede; esto refleja. */
async function revivir(): Promise<void> {
  audio.botonUi();
  if (!runActual) return;

  const boton = el<HTMLButtonElement>('botonRevivir');
  boton.disabled = true;
  el('revivirInfo').textContent = t().cargandoAnuncio;

  const c = await anuncios.ver('revivir', { runId: runActual.runId });

  if (c.concedido && c.revivirConcedido) {
    revivirRestantes = c.revivirRestantes;
    el('revivirInfo').textContent = '';
    mostrar('juego');
    motor?.revivir();
    return;
  }

  // Si ya no quedan revivires, no se le deja con un panel de un solo boton: se
  // cierra la partida y se le ensena el resumen. Un dialogo con una sola salida
  // es una molestia, no una opcion; es la misma razon por la que `alMorir` no
  // llega a ensenar este panel cuando no hay nada que ofrecer.
  if (c.revivirRestantes === 0) {
    revivirRestantes = 0;
    motor?.rendirse();
    return;
  }

  // No se concedio pero aun podria: se le dice por que y **se le deja la
  // salida**, con el boton de terminar y la opcion de reintentar.
  el('revivirInfo').textContent = c.motivo ?? t().anuncioSinConfirmar;
  boton.disabled = false;
}

/**
 * Revivir pagando monedas.
 *
 * **El servidor cobra y concede.** Aqui no se descuenta nada por adelantado:
 * lo que se pinta es lo que conteste, igual que con el x2 y con las apuestas.
 */
async function revivirPagando(): Promise<void> {
  audio.botonUi();
  if (!runActual) return;

  const boton = el<HTMLButtonElement>('botonRevivirMonedas');
  boton.disabled = true;
  el('revivirInfo').textContent = t().cargando;

  try {
    const r = await api.revivirConMonedas(runActual.runId, precioRevivir);
    perfil = { ...perfil, monedas: r.monedas };
    pintarInicio();

    if (!r.concedido) {
      el('revivirInfo').textContent = r.motivo ?? t().errorGenerico;
      boton.disabled = false;
      return;
    }

    revivirRestantes = r.revivirRestantes;
    el('revivirInfo').textContent = '';
    mostrar('juego');
    motor?.revivir();
  } catch (err) {
    console.warn('no pude revivir con monedas', err);
    el('revivirInfo').textContent = t().sinConexion;
    boton.disabled = false;
  }
}

/** El jugador no revive: se cierra la partida. */
function noRevivir(): void {
  audio.botonUi();
  el<HTMLButtonElement>('botonRevivir').hidden = false;
  el<HTMLButtonElement>('botonRevivirMonedas').hidden = true;
  motor?.rendirse();
}

async function alTerminar(r: ResultadoPartida): Promise<void> {
  ultimoResultado = r;
  const x = t();

  // Si el jugador se esta yendo al inicio, la partida se cierra igual (hay que
  // decirle al servidor como acabo) pero NO se le ensena el resumen: pidio ir
  // al inicio, no ver el resultado.
  const ensenarResumen = !volviendoAlInicio;

  el('distanciaFin').textContent = numero(r.distancia);
  el('monedasFin').textContent = numero(r.monedas);

  const cinta = el('cintaFin');
  const esRecord = r.distancia > perfil.record;
  const casi =
    !esRecord && perfil.record > 0 && r.distancia > perfil.record * 0.92;

  if (esRecord) {
    cinta.className = 'cinta record';
    cinta.textContent = x.nuevoRecord;
    el('detalleFin').textContent = '';
  } else if (casi) {
    cinta.className = 'cinta casi';
    cinta.textContent = x.casiLoLogras;
    el('detalleFin').textContent = x.casiDetalle(perfil.record - r.distancia);
  } else {
    cinta.className = '';
    cinta.textContent = '';
    el('detalleFin').textContent = '';
  }

  // Si la partida era de un reto, lo importante ya no es el record: es
  // compartirlo. El boton cambia para que el siguiente paso sea obvio.
  const botonRetar = el<HTMLButtonElement>('botonRetar');
  if (dueloEnCurso) {
    const duelo = dueloEnCurso;
    cinta.className = 'cinta record';
    cinta.textContent = td('retoListo');
    el('detalleFin').textContent = td('retoListoDetalle');
    botonRetar.textContent = td('compartirReto');
    botonRetar.onclick = () => compartirReto(duelo.duelId);
    dueloEnCurso = null;
  } else {
    botonRetar.textContent = td('retar');
    botonRetar.onclick = retar;
  }

  el<HTMLButtonElement>('botonDoblarPremio').hidden = true;
  if (ensenarResumen) mostrar('fin');

  // El servidor es quien decide si la partida vale y cuanto acredita. El cliente
  // solo ensena lo que vio; si el servidor dice otra cosa, manda el servidor.
  if (!runActual) return;
  // Se guarda CUAL partida se esta cerrando. Mas abajo, el `finally` solo
  // borra `runActual` si sigue siendo esta.
  //
  // Hace falta desde que se puede empezar otra partida sin pasar por el menu
  // ("Empezar de nuevo" en la pausa): cerrar es asincrono, asi que el
  // `finally` puede llegar DESPUES de que la partida nueva ya haya puesto su
  // identificador, y borrarselo. La siguiente muerte no se podria cerrar.
  const idCerrado = runActual.runId;
  try {
    const res = await api.terminarPartida({
      runId: runActual.runId,
      firma: runActual.firma,
      distancia: r.distancia,
      monedas: r.monedas,
      duracionMs: r.duracionMs,
      segmentos: r.segmentos,
      registroEntradas: r.registroEntradas,
    });
    if (res.aceptada) {
      perfil = { ...perfil, record: res.record, monedas: res.monedasTotales };
      pintarInicio();
      // Si esta partida gano un reto con apuesta, se le ofrece doblarlo. Es el
      // momento del spec, y es ahora: acaba de enterarse de que gano.
      if (ensenarResumen && res.dueloGanado) ofrecerDoblarPremio(res.dueloGanado);
    } else {
      // Partida rechazada: se dice, sin acusar. Puede ser un movil lento.
      el('detalleFin').textContent = x.errorGenerico;
      console.warn('partida rechazada:', res.motivoRechazo);
    }
  } catch (err) {
    console.error('no pude cerrar la partida', err);
    el('detalleFin').textContent = x.sinConexion;
  } finally {
    if (runActual?.runId === idCerrado) runActual = null;
    volviendoAlInicio = false;
    // Los anuncios del inicio cambian al acabar una partida: la caja puede
    // haberse gastado, o el x2 puede haberse consumido.
    void refrescarAnuncios();
  }
}

/**
 * Ofrece doblar el premio de un reto ganado.
 *
 * Este es el momento de los cuatro que **paga con dinero de otro jugador**, y
 * por eso es el unico que exige el aviso de verdad de Adsgram: con el modo
 * degradado encendido, el servidor no lo ofrece y aqui el boton desaparece
 * solo (ver DECISIONS.md D-011).
 */
function ofrecerDoblarPremio(duelo: {
  duelId: string;
  premio: number;
  moneda: string;
}): void {
  if (!cfgAnuncios?.activos) return;

  const boton = el<HTMLButtonElement>('botonDoblarPremio');
  boton.hidden = false;
  boton.disabled = false;
  boton.textContent = `${td('doblarPremio')} +${numero(duelo.premio)}`;

  boton.onclick = async () => {
    audio.botonUi();
    boton.disabled = true;
    boton.textContent = t().cargandoAnuncio;

    const c = await anuncios.ver('duelo_x2', { duelId: duelo.duelId });
    if (c.concedido) {
      perfil = { ...perfil, monedas: c.monedasTotales || perfil.monedas };
      pintarInicio();
      boton.hidden = true;
      el('detalleFin').textContent = t().cajaAbierta(c.monedas);
      return;
    }
    el('detalleFin').textContent = c.motivo ?? t().anuncioSinConfirmar;
    boton.hidden = true;
  };
}

function pausar(): void {
  motor?.pausar();
  mostrar('pausa');
}

/**
 * Empezar de nuevo desde la pausa.
 *
 * Cierra la partida de ahora **sin ensenar el resumen** —quien pulsa esto no
 * quiere ver como acabo, quiere otra— y arranca una. La bandera se pone con lo
 * que diga `abandonar`, igual que en `irAlInicio`, y `jugar()` la baja.
 *
 * El cierre hay que hacerlo: el servidor solo admite UNA partida abierta por
 * jugador, asi que empezar otra sin cerrar esta daria error.
 */
async function reiniciarPartida(): Promise<void> {
  volviendoAlInicio = motor?.abandonar() ?? false;
  await jugar();
}

function continuar(): void {
  audio.botonUi();
  mostrar('juego');
  motor?.reanudar();
}

function irAlInicio(): void {
  audio.botonUi();
  // La bandera vale lo que diga `abandonar`: es true solo si de verdad habia
  // una partida que cerrar, porque cerrarla llama a `alTerminar`, que mira
  // esta bandera para no ensenar el resumen de una partida que el jugador ha
  // decidido dejar.
  //
  // **Antes se ponia a true a secas, y ese era el fallo.** Si la partida ya
  // estaba cerrada —o si no habia ninguna, que es lo que pasa al volver del
  // creador de avatares— `abandonar` se vuelve sin llamar a `alTerminar`, que
  // era el unico sitio donde la bandera se bajaba. Se quedaba subida para
  // siempre y a partir de ahi **ninguna muerte ensenaba ya ningun panel**.
  volviendoAlInicio = motor?.abandonar() ?? false;
  pintarInicio();
  mostrar('inicio');
  void refrescarAnuncios();
}

// ---------------------------------------------------------------------------
// Anuncios recompensados
// ---------------------------------------------------------------------------

/**
 * Pregunta al servidor qué anuncios se pueden ofrecer y pinta los botones.
 *
 * **Los botones los decide el servidor**, no esta funcion: si dijera que sí
 * cuando el servidor dice que no, el jugador vería un anuncio para nada. Y al
 * revés, con los anuncios apagados no aparece ninguno.
 */
async function refrescarAnuncios(): Promise<void> {
  cfgAnuncios = await anuncios.config(runActual?.runId);
  const c = cfgAnuncios;
  const x = t();

  // El mismo x2 en dos sitios: el inicio y el fin de partida. Son dos botones
  // porque son dos pantallas, pero la decision es una sola y la toma el
  // servidor; aqui no puede haber dos criterios que se separen.
  for (const id of ['botonX2', 'botonX2Fin']) {
    const b = el<HTMLButtonElement>(id);
    b.hidden = !(c.activos && c.x2Disponible);
    b.disabled = false;
    b.textContent = x.jugarConX2;
  }

  const bCaja = el<HTMLButtonElement>('botonCaja');
  bCaja.hidden = !(c.activos && c.cajaDiariaLista);
  bCaja.disabled = false;
  bCaja.textContent = x.cajaDiaria(c.cajaDiariaMonedas);

  el('tarjetaAdmin').classList.toggle('visible', c.soyAdmin);
  if (c.soyAdmin) {
    void pintarAdmin();
    void pintarDuelosAdmin();
    void pintarAvisosAdmin();
  }
}

/** Ver el anuncio del x2 y, si se concede, empezar la partida con monedas dobles. */
async function jugarConX2(desde: 'botonX2' | 'botonX2Fin'): Promise<void> {
  audio.botonUi();
  const boton = el<HTMLButtonElement>(desde);
  boton.disabled = true;
  boton.textContent = t().cargandoAnuncio;

  const c = await anuncios.ver('x2_inicio');
  if (c.concedido && c.x2Listo) {
    await jugar(true);
    return;
  }
  boton.disabled = false;
  boton.textContent = t().jugarConX2;
  // El aviso va en la pantalla desde la que se toco, no siempre en el inicio:
  // escribirlo en una pantalla que no se esta viendo es no decir nada.
  const donde = desde === 'botonX2' ? 'subtituloInicio' : 'detalleFin';
  el(donde).textContent = c.motivo ?? t().anuncioSinConfirmar;
}

/** La caja diaria: una al día, y solo si el servidor la da. */
async function abrirCajaDiaria(): Promise<void> {
  audio.botonUi();
  const boton = el<HTMLButtonElement>('botonCaja');
  boton.disabled = true;
  boton.textContent = t().cargandoAnuncio;

  const c = await anuncios.ver('caja_diaria');
  if (c.concedido) {
    perfil = { ...perfil, monedas: c.monedasTotales || perfil.monedas + c.monedas };
    pintarInicio();
    el('subtituloInicio').textContent = t().cajaAbierta(c.monedas);
    boton.hidden = true;
    await refrescarAnuncios();
    return;
  }
  boton.disabled = false;
  boton.textContent = t().cajaDiaria(cfgAnuncios?.cajaDiariaMonedas ?? 0);
  el('subtituloInicio').textContent = c.motivo ?? t().anuncioSinConfirmar;
}

// ---------------------------------------------------------------------------
// La tarjeta del dueño
// ---------------------------------------------------------------------------

interface EstadoAdmin {
  activos: boolean;
  sinPostback: boolean;
  blockId: string;
  secretoConfigurado: boolean;
  urlDelAviso: string;
  embudo: Array<{
    momento: string;
    ofrecidos: number;
    aceptados: number;
    completados: number;
    tasaFinalizacion: number;
  }>;
  ingresoEstimadoUsd: number;
  aviso: string;
}

const NOMBRES_MOMENTO: Record<string, string> = {
  x2_inicio: 'Monedas ×2',
  revivir: 'Revivir',
  duelo_x2: 'Doblar reto',
  caja_diaria: 'Caja diaria',
};

/**
 * El estado de los anuncios, para el dueño, dentro del propio juego.
 *
 * El panel de administración es la fase 10. Esto existe para que la función no
 * nazca sin su tercera cara: el dueño gana la capacidad de encender y apagar
 * los anuncios, y tiene que poder hacerlo por los dos sitios por los que entra
 * —el bot y la app—, no solo por uno.
 */
async function pintarAdmin(): Promise<void> {
  const estado = el('adminEstado');
  const boton = el<HTMLButtonElement>('adminConmutar');

  if (modoDev) {
    estado.innerHTML =
      'En modo desarrollo no hay servidor: los anuncios están simulados y ' +
      'siempre encendidos.';
    el('adminAviso').textContent = '';
    boton.hidden = true;
    return;
  }

  try {
    const d = await pedir<EstadoAdmin>('/admin/ads');

    const lineas = [
      `Estado: <b>${d.activos ? '🟢 encendidos' : '🔴 apagados'}</b>`,
      `Bloque: <b>${d.blockId || 'sin configurar'}</b>`,
      `Secreto del aviso: <b>${d.secretoConfigurado ? '✅' : '❌ falta'}</b>`,
      `Ingreso estimado (7 días): <b>${d.ingresoEstimadoUsd.toFixed(2)} $</b>`,
    ];
    const conDatos = d.embudo.filter((f) => f.ofrecidos > 0);
    if (conDatos.length) {
      lineas.push('');
      for (const f of conDatos) {
        lineas.push(
          `${NOMBRES_MOMENTO[f.momento] ?? f.momento}: ${f.ofrecidos} → ` +
            `${f.aceptados} → <b>${f.completados}</b> ` +
            `(${Math.round(f.tasaFinalizacion * 100)} % acaban)`
        );
      }
    } else {
      lineas.push('', '<i>Todavía no se ha ofrecido ningún anuncio.</i>');
    }
    estado.innerHTML = lineas.join('<br>');
    el('adminAviso').textContent = d.aviso;

    boton.hidden = false;
    boton.disabled = false;
    boton.textContent = d.activos ? 'Apagar los anuncios' : 'Encender los anuncios';
    boton.onclick = async () => {
      audio.botonUi();
      boton.disabled = true;
      boton.textContent = t().cargando;
      try {
        await pedir<EstadoAdmin>('/admin/ads', { activos: !d.activos });
      } catch (err) {
        console.error('no pude cambiar el interruptor', err);
      }
      await refrescarAnuncios();
    };
  } catch (err) {
    console.error('no pude leer el estado de los anuncios', err);
    estado.textContent = t().sinConexion;
    boton.hidden = true;
  }
}

/**
 * Los duelos, en la tarjeta del dueño.
 *
 * El interruptor existía desde la fase 4 y **no había por dónde tocarlo**:
 * solo se leía. Así que los duelos llevaban apagados desde siempre, con el
 * botón de «Retar» a la vista contestando que no están disponibles.
 */
interface EstadoDuelos {
  activos: boolean;
  esperandoRival: number;
  resueltos: number;
}

async function pintarDuelosAdmin(): Promise<void> {
  const estado = el('adminDuelosEstado');
  const boton = el<HTMLButtonElement>('adminDuelos');

  if (modoDev) {
    estado.textContent = 'En modo desarrollo los duelos van simulados.';
    boton.hidden = true;
    return;
  }

  try {
    const d = await pedir<EstadoDuelos>('/admin/duelos');
    estado.innerHTML = [
      `Estado: <b>${d.activos ? '🟢 encendidos' : '🔴 apagados'}</b>`,
      `Esperando rival: <b>${numero(d.esperandoRival)}</b>`,
      `Resueltos: <b>${numero(d.resueltos)}</b>`,
    ].join('<br>');

    boton.hidden = false;
    boton.disabled = false;
    boton.textContent = d.activos ? 'Apagar los duelos' : 'Encender los duelos';
    boton.onclick = async () => {
      audio.botonUi();
      boton.disabled = true;
      boton.textContent = t().cargando;
      try {
        await pedir<EstadoDuelos>('/admin/duelos', { activos: !d.activos });
      } catch (err) {
        console.error('no pude cambiar los duelos', err);
      }
      await pintarDuelosAdmin();
    };
  } catch (err) {
    console.error('no pude leer el estado de los duelos', err);
    estado.textContent = t().sinConexion;
    boton.hidden = true;
  }
}

/**
 * Los avisos del bot, en la tarjeta del dueño.
 *
 * La tercera cara del interruptor: existe el comando `/avisos` en el bot y
 * existe `/admin/avisos` en la API, así que tiene que existir el botón. Un
 * endpoint sin botón, para el dueño, no existe (regla 1 de la casa).
 *
 * Lo interesante de enseñar aquí no es el interruptor, es **cuántos avisos hay
 * esperando**: con él apagado se siguen apuntando, así que ese número dice lo
 * que saldría en cuanto se encienda.
 */
interface EstadoAvisos {
  activos: boolean;
  pendientes: number;
  enviados: number;
  imposibles: number;
  ultimoError: string | null;
}

async function pintarAvisosAdmin(): Promise<void> {
  const estado = el('adminAvisosEstado');
  const boton = el<HTMLButtonElement>('adminAvisos');

  if (modoDev) {
    estado.textContent = 'En modo desarrollo no hay servidor: no se manda nada.';
    boton.hidden = true;
    return;
  }

  try {
    const d = await pedir<EstadoAvisos>('/admin/avisos');
    const lineas = [
      `Estado: <b>${d.activos ? '🟢 encendidos' : '🔴 apagados'}</b>`,
      `Esperando en la bandeja: <b>${numero(d.pendientes)}</b>`,
      `Enviados: <b>${numero(d.enviados)}</b>`,
    ];
    if (d.imposibles > 0) {
      // Casi siempre es gente que bloqueó al bot. No es un fallo nuestro, pero
      // conviene verlo: si de repente son muchos, algo pasa.
      lineas.push(`Sin poder entregar: <b>${numero(d.imposibles)}</b>`);
    }
    if (d.ultimoError) lineas.push(`<i>Último error: ${d.ultimoError}</i>`);
    estado.innerHTML = lineas.join('<br>');

    boton.hidden = false;
    boton.disabled = false;
    boton.textContent = d.activos ? 'Apagar los avisos' : 'Encender los avisos';
    boton.onclick = async () => {
      audio.botonUi();
      boton.disabled = true;
      boton.textContent = t().cargando;
      try {
        await pedir<EstadoAvisos>('/admin/avisos', { activos: !d.activos });
      } catch (err) {
        console.error('no pude cambiar los avisos', err);
      }
      await pintarAvisosAdmin();
    };
  } catch (err) {
    console.error('no pude leer el estado de los avisos', err);
    estado.textContent = t().sinConexion;
    boton.hidden = true;
  }
}

/**
 * Compartir el récord. Dos caminos, y se prefiere el primero:
 *
 *   1. **Stories de Telegram** con la imagen del récord. Una Story la ven todos
 *      los contactos del jugador sin que él tenga que elegir a nadie, que es la
 *      fricción que mata el resto de formas de compartir.
 *   2. Si el cliente es viejo y no soporta Stories, el selector de chats.
 *
 * La imagen la genera el servidor y viene firmada: así nadie puede fabricar una
 * tarjeta con el nombre de otro y una marca inventada.
 */
async function compartir(): Promise<void> {
  audio.botonUi();
  if (!ultimoResultado) return;

  const url = await urlTarjetaRecord();
  if (url && telegram.puedeCompartirHistoria) {
    telegram.compartirHistoria(url, {
      text: '🚀 MoonRush',
      widget_link: {
        url: enlaceDeInvitacion(),
        name: 'Jugar',
      },
    });
    return;
  }
  telegram.compartirInline('');
}

/** El enlace de invitación del jugador. Va en la Story como enlace clicable. */
function enlaceDeInvitacion(): string {
  // Lo monta el SERVIDOR y viene en el perfil. Aqui se escribia a mano el
  // nombre del bot, y el 23-oct-2026 resulto que el nombre escrito no era el
  // del bot que existe: cada enlace compartido en una Story llevaba a ninguna
  // parte. El servidor es el unico que sabe como se llama el bot de verdad.
  return perfil.enlaceInvitacion;
}

// ---------------------------------------------------------------------------
// Arranque
// ---------------------------------------------------------------------------

async function arrancar(): Promise<void> {
  telegram.preparar();
  // Se ESPERA el idioma antes de pintar. Los textos ya no vienen todos en la
  // primera carga: el espanol si, y los otros tres se van a buscar. Pintar
  // antes de que llegue significaria ensenar el menu en espanol y cambiarlo a
  // la vista; esperar un fichero de 5 KB del mismo dominio, no.
  await fijarIdioma(telegram.idioma);
  pintarTextos();

  if (modoDev) {
    el('avisoDev').classList.add('visible');
  }

  el('botonJugar').addEventListener('click', () => void jugar());
  el('botonOtraVez').addEventListener('click', () => void jugar());
  el('botonContinuar').addEventListener('click', continuar);
  el('botonX2').addEventListener('click', () => void jugarConX2('botonX2'));
  el('botonX2Fin').addEventListener('click', () => void jugarConX2('botonX2Fin'));
  el('botonCaja').addEventListener('click', () => void abrirCajaDiaria());
  el('botonRevivir').addEventListener('click', () => void revivir());
  el('botonRevivirMonedas').addEventListener('click', () => void revivirPagando());
  el('botonNoRevivir').addEventListener('click', noRevivir);
  el('botonCompartir').addEventListener('click', () => void compartir());
  el('botonRetar').addEventListener('click', retar);
  el('botonAvatar').addEventListener('click', () => void abrirAvatar());
  el('avatarGuardar').addEventListener('click', () => void guardarAvatar());
  el('avatarVolver').addEventListener('click', irAlInicio);
  el('botonInvitar').addEventListener('click', () => void abrirInvitar());
  el('botonClan').addEventListener('click', () => void abrirClan());
  el('botonPozo').addEventListener('click', () => void abrirPozo());
  el('pozoConectar').addEventListener('click', () => void conectarMonedero());
  el('pozoQuitar').addEventListener('click', () => void quitarMonedero());
  el('pozoVolver').addEventListener('click', irAlInicio);
  el('botonClanSalir').addEventListener('click', () => void salirDelClan());
  el('botonClanVolver').addEventListener('click', irAlInicio);
  el('botonApuestaCancelar').addEventListener('click', () => {
    audio.botonUi();
    mostrar('fin');
  });
  el('botonAceptarReto').addEventListener('click', () => void aceptarReto());
  el('botonRetoAhoraNo').addEventListener('click', () => void rechazarReto());
  el('botonTutorialListo').addEventListener('click', () => {
    audio.botonUi();
    cerrarTutorial();
  });
  el('botonPausa').addEventListener('click', pausar);
  el('botonPausaReiniciar').addEventListener('click', () => void reiniciarPartida());
  el('botonPausaInicio').addEventListener('click', irAlInicio);
  el('botonFinInicio').addEventListener('click', irAlInicio);
  el('refCompartir').addEventListener('click', () => void compartirEnlace());
  el('refCopiar').addEventListener('click', () => void copiarEnlace());
  el('refVolver').addEventListener('click', irAlInicio);

  // Si el jugador vuelve de otra app estando en el juego, se queda en pausa a
  // proposito: aparecer de golpe a toda velocidad es una muerte injusta.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden && hud.classList.contains('visible')) pausar();
  });

  telegram.alCambiarTema(pintarTextos);

  try {
    perfil = await api.perfil();
  } catch (err) {
    console.warn('no pude cargar el perfil, sigo con valores a cero', err);
  }
  pintarInicio();
  mostrar('inicio');

  // Si alguien le ha retado, eso va ANTES que los anuncios: es la razon por
  // la que ha abierto el juego.
  await mirarSiMeHanRetado();

  // Los anuncios se preguntan DESPUES de pintar el inicio: si el servidor
  // tarda, el jugador ya puede darle a Jugar. Un boton de anuncio que aparece
  // dos segundos tarde es mejor que una pantalla en blanco.
  await refrescarAnuncios();
}

void arrancar();

// ---------------------------------------------------------------------------
// Duelos
// ---------------------------------------------------------------------------

/**
 * Retar a un amigo. Son dos pasos, y el orden importa:
 *
 *   1. se crea el reto y **se juega** (hay que dejar una marca que superar)
 *   2. cuando esa partida acaba, se comparte
 *
 * No se puede compartir antes de jugar: un reto sin marca no es un reto, y
 * compartir "a ver si me superas, pero todavía no sé cuánto" no engancha a
 * nadie.
 */
/**
 * Las cantidades que se ofrecen.
 *
 * Pequeñas a propósito, y esto es producto y no pereza: **quien acepta pone lo
 * mismo**. Una apuesta que tu amigo no puede igualar es un reto que nadie
 * acepta, y el reto sin aceptar no le sirve a nadie. Por eso la primera opción
 * es siempre «sin apuesta»: es la que puede aceptar quien acaba de instalar el
 * juego y tiene cero monedas.
 */
const APUESTAS = [50, 200, 1000];

/** Retar: primero se pregunta cuánto, y luego ya se crea. */
function retar(): void {
  audio.botonUi();
  el('apuestaSaldo').textContent =
    `${td('tienes')} ${numero(perfil.monedas)} ${t().monedas.toLowerCase()}`;
  el('apuestaAviso').textContent = '';
  pintarFichasDeApuesta();
  mostrar('apuesta');
}

function pintarFichasDeApuesta(): void {
  const cont = el('apuestaFichas');
  cont.textContent = '';

  const sin = document.createElement('button');
  sin.className = 'ficha tocable';
  sin.textContent = td('sinApuesta');
  sin.addEventListener('click', () => void crearReto(0));
  cont.appendChild(sin);

  for (const cantidad of APUESTAS) {
    const boton = document.createElement('button');
    const puede = perfil.monedas >= cantidad;
    boton.className = `ficha tocable${puede ? '' : ' sinsaldo'}`;
    // Sin emoji de moneda: el 🪙 es de los nuevos y en algunas fuentes sale
    // como un cuadrado vacio. Visto en la propia pantalla. El numero solo se
    // entiende igual, que para eso el titulo dice de que se habla.
    boton.textContent = numero(cantidad);
    // Las que no alcanzan se enseñan igual, apagadas: ver lo que se podría
    // apostar es parte de querer conseguirlo. Pero al tocarlas se explica,
    // en vez de no hacer nada —un botón que no responde parece roto—.
    boton.addEventListener('click', () => {
      if (puede) {
        void crearReto(cantidad);
        return;
      }
      audio.botonUi();
      el('apuestaAviso').textContent = td('sinSaldo');
    });
    cont.appendChild(boton);
  }
}

async function crearReto(apuesta: number): Promise<void> {
  audio.botonUi();
  const boton = el<HTMLButtonElement>('botonRetar');
  boton.disabled = true;
  boton.textContent = td('creandoReto');
  el('apuestaAviso').textContent = td('creandoReto');

  try {
    // **El saldo lo descuenta el SERVIDOR al crear.** Aquí no se resta nada:
    // si se restara, el día que el servidor dijera que no, el jugador vería
    // menos monedas de las que tiene.
    dueloEnCurso = await duelos.crear(apuesta);
    if (apuesta > 0) perfil = { ...perfil, monedas: perfil.monedas - apuesta };
    pintarInicio();
    el('detalleFin').textContent = td('juegaTuReto');
    await jugar();
  } catch (err) {
    console.error('no pude crear el reto', err);
    const mensaje = String(err);
    // El aviso va donde el jugador está mirando, que es la pantalla de la
    // apuesta, no la de fin de partida que quedó debajo.
    el('apuestaAviso').textContent = mensaje.includes('503')
      ? td('duelosApagados')
      : mensaje.includes('saldo')
        ? td('sinSaldo')
        : t().errorGenerico;
    dueloEnCurso = null;
  } finally {
    boton.disabled = false;
    boton.textContent = td('retar');
  }
}

/** Abre el selector de chats de Telegram con la tarjeta del reto. */
function compartirReto(duelId: string): void {
  audio.botonUi();
  // La consulta en línea vacía hace que el bot ofrezca los retos del jugador,
  // con su tarjeta ya generada. Mandar el id en la consulta sería más directo,
  // pero obligaría a que el bot confiara en un texto que escribe el cliente.
  telegram.compartirInline('');
  void duelId;
}


// ---------------------------------------------------------------------------
// Pantalla de invitar
// ---------------------------------------------------------------------------

/**
 * La pantalla tiene que explicar las condiciones, no solo enseñar un enlace.
 *
 * Un invitador que no sabe que su amigo tiene que jugar diez partidas cree que
 * el sistema no le paga, y lo cuenta. Decirlo claro cuesta dos frases y evita
 * la peor conversación posible: la de alguien convencido de que le has robado.
 */
async function abrirInvitar(): Promise<void> {
  audio.botonUi();
  mostrar('invitar');
  el('refEnlace').textContent = t().cargando;

  try {
    const d = await referidos.mios();

    el('refEnlace').textContent = d.enlace;
    el('refPendientes').querySelector('.n')!.textContent = numero(d.pendientes);
    el('refActivados').querySelector('.n')!.textContent = numero(d.activados);
    el('refGanadas').querySelector('.n')!.textContent = numero(d.monedasGanadas);

    el('refExplica').textContent =
      d.faltanParaSiguiente !== null && d.siguienteHito
        ? `Te faltan ${d.faltanParaSiguiente} para: ${d.siguienteHito}`
        : 'Has conseguido todos los hitos';

    el('refHitos').innerHTML = d.hitos
      .map(
        (h) =>
          `<div class="hito ${h.conseguido ? 'hecho' : ''}">` +
          `<div class="marca">${h.conseguido ? '✓' : h.necesarios}</div>` +
          `<div>${h.descripcion}</div></div>`
      )
      .join('');

    // Las condiciones, en texto llano y con los números que manda el servidor,
    // no repetidos aquí: si un día cambian, cambian en un solo sitio.
    el('refLetraPequena').innerHTML =
      `Ganas <b>${numero(d.monedasPorActivacion)} monedas</b> cuando quien invitas ` +
      `juega <b>${d.partidasParaActivar} partidas</b> en sus primeras 48 horas. ` +
      `No se cobra por registrarse: así nadie puede inventarse cuentas.`;
  } catch (err) {
    console.error('no pude cargar los referidos', err);
    el('refEnlace').textContent = t().sinConexion;
  }
}

async function compartirEnlace(): Promise<void> {
  audio.botonUi();
  // El enlace lo da el SERVIDOR (`/referrals`), que es quien sabe como se
  // llama el bot de verdad. Construirlo aqui fue justo el otro fallo de este
  // dia: el nombre estaba escrito a mano y no era el que existe.
  const enlace = el('refEnlace').textContent?.trim() ?? '';
  if (!enlace) return;

  const salida = await telegram.compartirEnlace(enlace, td('invitaTexto'));
  if (salida !== 'no') return;

  // Ni hoja del sistema ni Telegram: al menos que se lo lleve copiado, y que
  // se entere de que eso fue lo que paso.
  const aviso = el('refCompartir');
  await copiarEnlace();
  aviso.textContent = td('enlaceCopiado');
  window.setTimeout(() => {
    aviso.textContent = td('compartir');
  }, 2200);
}

async function copiarEnlace(): Promise<void> {
  audio.botonUi();
  const enlace = el('refEnlace').textContent ?? '';
  const boton = el('refCopiar');
  try {
    await navigator.clipboard.writeText(enlace);
    boton.textContent = '✓ Copiado';
  } catch {
    // En algunos WebView de Telegram el portapapeles está bloqueado. En vez de
    // no hacer nada, se selecciona el texto para que se pueda copiar a mano.
    const rango = document.createRange();
    rango.selectNodeContents(el('refEnlace'));
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(rango);
    boton.textContent = 'Selecciónalo y copia';
  }
  window.setTimeout(() => {
    boton.textContent = 'Copiar';
  }, 2200);
}
